import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Typography,
  Link
} from "@mui/material";
import { useNav } from "../../context/NavProvider";
import { useClient } from "../../context/ClientProvider";
import ScriptLoader from "../account/ScriptLoader";
import CallMeDialogLogin from "./CallMeDialogLogin";
import CallMeDialog from "./CallMeDialog";
import { useAuth } from "../../context/AuthProvider";

function ContactUsPage() {
  const { setShowFooter } = useNav();
  const { useGetConfig } = useClient();
  const { clientConfig } = useGetConfig();
  const { accessToken } = useAuth();
  const [hours, setHours] = useState([]);
  const [openCallDialog, setOpenCallDialog] = useState(false);
  const contactUsDataPerClient = clientConfig?.layout?.unauthenticatedPages.contactUs;

  useEffect(() => {
    const daily = clientConfig?.layout?.callMe?.dailyAttributes;

    if (Array.isArray(daily)) {
      setHours(daily);
    }
  }, [clientConfig]);

  useEffect(() => {
    setShowFooter(true);
  }, [setShowFooter]);

  const handleOpenCallback = () => {
    setOpenCallDialog(true);
  };

  const handleCloseCallback = () => {
    setOpenCallDialog(false);
  };

  const getGroupedHours = () => {
    if (hours.length < 6) return []; // safety check

    const weekdayOpening = hours[0]?.openingTime;
    const weekdayClosing = hours[0]?.closingTime;

    const allWeekdaysSame = hours.slice(0, 5).every(
      h => h.openingTime === weekdayOpening && h.closingTime === weekdayClosing
    );

    const grouped = [];

    if (allWeekdaysSame) {
      grouped.push({
        day: "Monday - Friday",
        openingTime: weekdayOpening,
        closingTime: weekdayClosing
      });
    } else {
      grouped.push(...hours.slice(0, 5)); // fallback: show all weekdays individually
    }

    // Add Saturday
    if (hours[5]) {
      grouped.push(hours[5]);
    }

    // Add static Sunday + Bank Holidays
    grouped.push({ day: "Sunday", openingTime: "Closed", closingTime: "" });
    grouped.push({ day: "Bank Holidays", openingTime: "Closed", closingTime: "" });

    return grouped;
  };

  return (
    <>
      <ScriptLoader />
      <Container sx={{ mt: 4, mb: 7 }}>
        <Box sx={{ pb: 2 }}>
          <Typography variant='h1' fontWeight='bold' fontSize={32} sx={{ py: 4 }}>
            Contact us
          </Typography>
          {contactUsDataPerClient?.liveChat?.enabled && (
            <>
          <Typography variant='h2' fontWeight='bold' fontSize={25} sx={{ py: 1 }}>
                Live Chat / Send a Message
          </Typography>
          <Typography sx={{ pt: 1, pb: 3 }}>Start a conversation using Live Chat. During online hours we'll message you back and support you with your query real time.
              </Typography>
            </>
          )}
          {clientConfig?.layout.callMe.active && (
            <>
          <Typography variant='h2' fontWeight='bold' fontSize={26} sx={{ py: 1 }}>
            Call Me
          </Typography>
          <Typography>Use our 'Call Me' function and we'll ring you back at a time that suits you.</Typography>

            <Button
              onClick={handleOpenCallback}
              sx={{
                flex: 1,
                mt: 1,
                mb: 3,
                borderRadius: "0",
                backgroundColor: "primary.dark",
                width: { xs: "15em", lg: "20em" },
                color: "#fff",
                "&:hover": {
                  backgroundColor: "primary.light",
                  color: "primary.dark",
                },
              }}>Call me</Button>
            </>)}
          {!accessToken && clientConfig?.layout.callMe.active ? (
            <CallMeDialogLogin
              open={openCallDialog}
              onClose={handleCloseCallback}
            />

          ) : (
            <CallMeDialog
              open={openCallDialog}
              onClose={handleCloseCallback}
            />
          )}
          {contactUsDataPerClient?.phoneUs?.enabled && (
            <>
          <Typography variant='h2' fontWeight='bold' fontSize={26} sx={{ py: 2 }}>
            Phone us
              </Typography>
            <Typography sx={{ pt: 1, pb: 2, mb: 3 }}>
              Call the team directly on <Link href={`tel:${contactUsDataPerClient?.phoneUs?.phoneNumber}`}>{contactUsDataPerClient?.phoneUs?.phoneNumber}</Link>
              </Typography>
            </>
          )}
          {contactUsDataPerClient?.loanApproval?.enabled && (
            <>
          <Typography variant='h2' fontWeight='bold' fontSize={26} sx={{ py: 1 }}>
            Was your loan approved before December 2021?
          </Typography>
          <Typography sx={{ pt: 1, pb: 1 }}>
            If you have taken out a <b>personal loan</b> or a <b>point of sale loan</b> (through one of our approved partners) <b>before</b> 13th December 2021,
            please use the following contact details.
          </Typography>
          <Typography sx={{ pt: 1, pb: 1 }}>
            Please note, if you are contacting us about additional borrowing on your existing loan, we do not offer this. However, you may be eligible to apply for another loan with us.
            We are unable to carry out phone applications but you can apply via our <Link rel="noopener noreferrer" href='https://www.intrum.co.uk/'>website</Link> or one of our partners.
              </Typography>


          <Typography sx={{ pt: 1, pb: 1, mb: 3 }}>
            Call the team directly on <Link href={`tel:${contactUsDataPerClient?.phoneUs?.phoneNumber}`}>{contactUsDataPerClient?.phoneUs?.phoneNumber}.</Link>
          </Typography>
            </>)}
          {contactUsDataPerClient?.openingHours?.enabled && (
            <>
          <Typography variant='h2' fontWeight='bold' fontSize={26} sx={{ py: 1 }}>
            Opening hours
          </Typography>
          <Box sx={{ pb: 5 }}>
            <Typography aria-label="opening hours table">
              {getGroupedHours().map((row, item) => (
                <Box
                  key={item}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    maxWidth: 400, // adjust width as needed
                    mb: 1,
                  }}
                >
                  <Typography>{row.day}</Typography>
                  {row.openingTime === "Closed" ? (
                    <Typography>Closed</Typography>
                  ) : (
                    <Typography>
                      {row.openingTime} - {row.closingTime}
                    </Typography>
                  )}
                </Box>
              ))}
            </Typography>
          </Box>
            </>)}
          {contactUsDataPerClient?.writeOrSendEmail?.enabled && (
            <>
          <Typography variant='h2' fontWeight='bold' fontSize={26}>
            Write to us or send us an email
          </Typography>
          <Typography sx={{ pt: 1 }}>
            Email the team (making sure to detail your full name and your account Reference Number) at:
              </Typography>
            <Link href={`mailto:${contactUsDataPerClient?.writeOrSendEmail.emailAddress}`}>
              {contactUsDataPerClient?.writeOrSendEmail.emailAddress}
            </Link>

              <Typography sx={{ pt: 1 }}>If you're looking to send details in the post, again please include your full name and your Reference Number. Our address is:</Typography>
              <Typography sx={{ pt: 2 }}>{contactUsDataPerClient?.writeOrSendEmail.address1}</Typography>
              <Typography sx={{ pt: 2 }}>{contactUsDataPerClient?.writeOrSendEmail?.address2}</Typography>
              <Typography sx={{ pt: 2 }}>{contactUsDataPerClient?.writeOrSendEmail?.address3}</Typography>
              <Typography sx={{ pt: 2 }}>{contactUsDataPerClient?.writeOrSendEmail?.postCode}</Typography>

            </>
          )}        
        </Box>
      </Container>
    </>
  );
}

export default ContactUsPage;
